'use client';

import type { FC } from 'react';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { SupportUserMessage } from '../../services/generated/Api';
import {
  SupportUserMessageAuthorEnum,
  SupportUserMessageKindEnum,
} from '../../services/generated/Api';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { cn } from '../../utils/cn';
import { Spinner } from '../ui/Spinner';
import { SupportAttachment } from './SupportAttachment';
import translations from './SupportChat.translations';

export interface PendingMessage {
  localId: string;
  text: string | null;
  fileName: string | null;
  previewUrl: string | null;
  hasFailed: boolean;
}

interface Bubble {
  key: string;
  author: SupportUserMessageAuthorEnum;
  createdAt: string;
  messages: SupportUserMessage[];
}

interface Row {
  dayLabel: string | null;
  bubble: Bubble;
}

/** Album parts arrive as separate messages sharing a media group. */
function toBubbles(messages: SupportUserMessage[]): Bubble[] {
  const bubbles: Bubble[] = [];

  for (const message of messages) {
    const previous = bubbles[bubbles.length - 1];
    const sameAlbum =
      previous != null &&
      message.mediaGroupId != null &&
      previous.messages[0].mediaGroupId === message.mediaGroupId;

    if (sameAlbum) {
      previous.messages.push(message);
      continue;
    }

    bubbles.push({
      key: message.id,
      author: message.author,
      createdAt: message.createdAt,
      messages: [message],
    });
  }

  return bubbles;
}

const BubbleBody: FC<{ bubble: Bubble }> = ({ bubble }) => {
  const parts = bubble.messages;
  const texts = parts.map((message) => message.text).filter((text): text is string => !!text);
  const media = parts.filter((message) => message.kind !== SupportUserMessageKindEnum.Text);

  return (
    <div className="flex flex-col gap-1.5">
      {media.length > 1 ? (
        <div className="grid grid-cols-2 gap-1">
          {media.map((message) => (
            <SupportAttachment key={message.id} message={message} />
          ))}
        </div>
      ) : (
        media.map((message) => <SupportAttachment key={message.id} message={message} />)
      )}
      {texts.map((text, index) => (
        <span key={index} className="whitespace-pre-wrap break-words text-[15px] leading-snug">
          {text}
        </span>
      ))}
    </div>
  );
};

interface SupportMessageListProps {
  messages: SupportUserMessage[];
  pending: PendingMessage[];
  canLoadOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder: () => void;
  onRetryPending: (localId: string) => void;
  onAtBottomChange: (atBottom: boolean) => void;
}

export const SupportMessageList: FC<SupportMessageListProps> = ({
  messages,
  pending,
  canLoadOlder,
  isLoadingOlder,
  onLoadOlder,
  onRetryPending,
  onAtBottomChange,
}) => {
  const { t, lang } = useLocalTranslation(translations);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const previousHeightRef = useRef(0);
  const previousTopIdRef = useRef<string | null>(null);

  const dayFormatter = useMemo(
    () => new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long' }),
    [lang],
  );
  const timeFormatter = useMemo(
    () => new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }),
    [lang],
  );

  const rows = useMemo<Row[]>(() => {
    let currentDay = '';
    return toBubbles(messages).map((bubble) => {
      const day = new Date(bubble.createdAt).toDateString();
      const dayLabel = day === currentDay ? null : dayFormatter.format(new Date(bubble.createdAt));
      currentDay = day;
      return { dayLabel, bubble };
    });
  }, [messages, dayFormatter]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const topId = messages[0]?.id ?? null;
    const grewUpwards = previousTopIdRef.current !== null && topId !== previousTopIdRef.current;

    if (grewUpwards) {
      element.scrollTop += element.scrollHeight - previousHeightRef.current;
    } else if (isPinnedRef.current) {
      element.scrollTop = element.scrollHeight;
    }

    previousTopIdRef.current = topId;
    previousHeightRef.current = element.scrollHeight;
  });

  const handleScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    const isPinned = distanceFromBottom < 80;
    if (isPinned !== isPinnedRef.current) {
      isPinnedRef.current = isPinned;
      onAtBottomChange(isPinned);
    }

    if (element.scrollTop < 120 && canLoadOlder && !isLoadingOlder) {
      onLoadOlder();
    }
  }, [canLoadOlder, isLoadingOlder, onLoadOlder, onAtBottomChange]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto overflow-x-hidden [overflow-anchor:auto] [-webkit-overflow-scrolling:touch] px-4 py-3"
    >
      {canLoadOlder && (
        <div className="flex justify-center pb-3">
          {isLoadingOlder ? (
            <Spinner size="s" />
          ) : (
            <button
              type="button"
              onClick={onLoadOlder}
              className="rounded-full bg-fill-tertiary px-4 py-2 text-[13px] text-label-secondary active:opacity-70"
            >
              {t('loadOlder')}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {rows.map(({ dayLabel, bubble }) => (
          <div key={bubble.key} className="flex flex-col gap-2">
            {dayLabel != null && (
              <div className="self-center rounded-full bg-fill-tertiary px-3 py-1 text-[11px] text-label-tertiary">
                {dayLabel}
              </div>
            )}

            {bubble.author === SupportUserMessageAuthorEnum.System ? (
              <div className="self-center px-4 text-center text-[12px] text-label-tertiary">
                {bubble.messages.map((message) => message.text).join(' ')}
              </div>
            ) : (
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-3 py-2',
                  bubble.author === SupportUserMessageAuthorEnum.User
                    ? 'self-end rounded-br-md bg-accent-blue text-white'
                    : 'self-start rounded-bl-md bg-bg-tertiary text-label-primary',
                )}
              >
                {bubble.author === SupportUserMessageAuthorEnum.Operator && (
                  <div className="mb-0.5 text-[11px] font-semibold text-accent-blue">
                    {t('operator')}
                  </div>
                )}
                <BubbleBody bubble={bubble} />
                <div
                  className={cn(
                    'mt-0.5 text-right text-[10px] tabular-nums',
                    bubble.author === SupportUserMessageAuthorEnum.User
                      ? 'text-white/60'
                      : 'text-label-tertiary',
                  )}
                >
                  {timeFormatter.format(new Date(bubble.createdAt))}
                </div>
              </div>
            )}
          </div>
        ))}

        {pending.map((item) => (
          <div
            key={item.localId}
            className={cn(
              'max-w-[85%] self-end rounded-2xl rounded-br-md px-3 py-2',
              item.hasFailed ? 'bg-accent-red/20 text-label-primary' : 'bg-accent-blue/60 text-white',
            )}
          >
            <div className="flex flex-col gap-1.5">
              {item.previewUrl != null && (
                <img
                  src={item.previewUrl}
                  alt={item.fileName ?? ''}
                  className="max-h-[240px] w-full max-w-[240px] rounded-xl object-cover"
                />
              )}
              {item.previewUrl == null && item.fileName != null && (
                <span className="truncate text-[13px] opacity-80">{item.fileName}</span>
              )}
              {item.text != null && (
                <span className="whitespace-pre-wrap break-words text-[15px] leading-snug">
                  {item.text}
                </span>
              )}
            </div>
            {item.hasFailed ? (
              <button
                type="button"
                onClick={() => onRetryPending(item.localId)}
                className="mt-1 text-[11px] font-semibold text-accent-red"
              >
                {t('sendFailed')} · {t('retry')}
              </button>
            ) : (
              <div className="mt-0.5 text-right text-[10px] text-white/60">{t('sending')}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
